import { isArray } from 'betterplace-utils';
import { useEffect, useMemo, useRef } from 'react';
import buildGoogleWebfontUrl from './buildGoogleWebfontUrl';
import type { GoogleFonts } from 'src/app/api/fonts/types';
type UseGoogleFontsProps = {
  fonts?: GoogleFonts[number];
} | {
  fonts?: GoogleFonts;
};
function useGoogleFonts({
  fonts: fonts_
}: UseGoogleFontsProps) {
  const linksRef = useRef<HTMLLinkElement[]>([]);
  const asArray = useMemo(() => fonts_ ? isArray(fonts_) ? fonts_ : [fonts_] : [], [fonts_]);
  const aggregated = useMemo(() => {
    if (!asArray.length) return [];
    const fontsMap = new Map<string, Set<number>>();
    for (const {
      family,
      weights
    } of asArray) {
      const set = fontsMap.get(family) ?? new Set<number>();
      for (const weight of weights) {
        set.add(weight);
      }
      fontsMap.set(family, set);
    }
    return [...fontsMap.entries()].map(([family, set]) => ({
      family,
      weights: [...set.values()]
    }));
  }, [asArray]);
  const hrefs = useMemo(() => aggregated.map(({
    family,
    weights
  }) => buildGoogleWebfontUrl(family, weights)), [aggregated]);
  useEffect(() => {
    if (!hrefs) return;
    for (const href of hrefs) {
      if (!href) continue;
      const id = new URL(href).searchParams.get('family') ?? '';
      const existingLink = document.getElementById(id) as HTMLLinkElement | null;
      if (existingLink) {
        if (existingLink.href === href) continue;
        document.head.removeChild(existingLink);
      }
      const link = document.createElement('link');
      link.href = href;
      link.id = id;
      link.rel = 'stylesheet';
      linksRef.current.push(link);
      document.head.appendChild(link);
    }
  }, [hrefs]);
  useEffect(() => {
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      linksRef.current.forEach(link => {
        if (!document.head.contains(link)) return;
        document.head.removeChild(link);
      });
    };
  }, []);
}
export default useGoogleFonts;